@if (isLoading && !errorMessage) {
  <div class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8343f] mx-auto"></div>
      <p class="mt-4 text-sm text-gray-600">Cargando factura para imprimir...</p>
    </div>
  </div>
} @else if (errorMessage) {
  <div class="flex items-center justify-center min-h-screen">
    <div class="text-center max-w-md p-6">
      <svg class="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
      <h2 class="text-xl font-semibold text-gray-900 mb-2">Error al cargar la factura</h2>
      <p class="text-sm text-gray-600 mb-4">{{ errorMessage }}</p>
      <p class="text-xs text-gray-500">Esta ventana se cerrará automáticamente...</p>
    </div>
  </div>
} @else if (factura) {
  <div class="print-container">
    <!-- Header -->
    <div class="header">
      <div class="logo-section">
        <h1 class="company-name">Ticket Éxito</h1>
        <p class="company-info">Sistema de venta de tickets</p>
      </div>
      <div class="invoice-info">
        <h2>FACTURA</h2>
        <p><strong>No:</strong> {{ factura.facturaid }}</p>
        <p><strong>Fecha:</strong> {{ formatDate(factura.fecha_emision) }}</p>
        <p><strong>Estado:</strong> <span class="status">{{ factura.estado }}</span></p>
      </div>
    </div>

    <!-- Evento Info -->
    <div class="section">
      <h3>Información del Evento</h3>
      <div class="info-grid">
        <div><strong>Evento:</strong> {{ factura.evento.nombre }}</div>
        <div><strong>Lugar:</strong> {{ factura.evento.lugar }}</div>
        <div><strong>Estado:</strong> {{ factura.evento.estado }}</div>
      </div>
    </div>

    <!-- Cliente Info -->
    <div class="section">
      <h3>Información del Cliente</h3>
      <div class="info-grid">
        <div><strong>Nombre:</strong> {{ factura.contacto.firstname }} {{ factura.contacto.lastname }}</div>
        <div><strong>Email:</strong> {{ factura.contacto.email }}</div>
        <div><strong>Teléfono:</strong> {{ factura.contacto.phone }}</div>
      </div>
    </div>

    <!-- Facturación Info -->
    <div class="section">
      <h3>Datos de Facturación</h3>
      <div class="info-grid">
        <div><strong>Nombre/Razón Social:</strong> {{ factura.informacionfactura.nombre }}</div>
        <div><strong>{{ factura.informacionfactura.tipo_identificacion.descripcion }}:</strong> {{ factura.informacionfactura.identificacion }}</div>
        <div><strong>Dirección:</strong> {{ factura.informacionfactura.direccion }}</div>
        <div><strong>Email:</strong> {{ factura.informacionfactura.email }}</div>
      </div>
    </div>

    <!-- Tickets Table -->
    <div class="section">
      <h3>Detalle de Tickets</h3>
      <table class="tickets-table">
        <thead>
          <tr>
            <th>QR</th>
            <th>Detalle</th>
            <th class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          @for (item of factura.items; track item.ticketid) {
            <tr>
              <td>
                <div class="qr-cell">
                  @if (qrImages.get(item.ticketid)) {
                    <img
                      class="qr-image"
                      [src]="qrImages.get(item.ticketid)"
                      [alt]="'QR ticket ' + item.ticketid"
                    />
                  }
                  <span class="qr-text">{{ getTicketDetalle(item.ticketid)?.qr || '-' }}</span>
                </div>
              </td>
              <td>
                <div style="display: flex; flex-direction: column; gap: 2px; font-size: 0.9em;">
                  @if (getTicketDetalle(item.ticketid)?.funcion?.subevento?.nombre) {
                    <span><strong>Subevento:</strong> {{ getTicketDetalle(item.ticketid)!.funcion!.subevento!.nombre }}</span>
                  }
                  @if (getTicketDetalle(item.ticketid)?.tipoticket?.nombre_localidad) {
                    <span><strong>Localidad:</strong> {{ getTicketDetalle(item.ticketid)!.tipoticket!.nombre_localidad }}</span>
                  }
                  <span><strong>Precio:</strong> {{ item.precioTipoTicket.nombre_precio }}</span>
                  @if (item.precioTipoTicket.tipoprecio) {
                    <span><strong>Tipo:</strong> {{ item.precioTipoTicket.tipoprecio }}</span>
                  }
                  @if (getTicketDetalle(item.ticketid)?.secuencial) {
                    <span><strong>Asiento:</strong> {{ getTicketDetalle(item.ticketid)!.secuencial }}</span>
                  }
                </div>
              </td>
              <td class="text-right">${{ (item.precioTipoTicket.precio_base + item.precioTipoTicket.precio_cargo_servicio + (item.precioTipoTicket.precio_servicio_administrativo || 0)).toFixed(2) }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Totales -->
    <div class="section totales">
      <div class="totales-grid">
        <div class="total-row">
          <span>Subtotal:</span>
          <span>${{ (factura.totales.subtotal_tickets + factura.totales.subtotal_cargo_servicios).toFixed(2) }}</span>
        </div>
        <div class="total-row">
          <span>IVA:</span>
          <span>${{ (factura.totales.total_iva_tickets + factura.totales.total_iva_cargo_servicios).toFixed(2) }}</span>
        </div>
        <div class="total-row final">
          <span><strong>Total:</strong></span>
          <span><strong>${{ factura.totales.total_factura.toFixed(2) }}</strong></span>
        </div>
        @if (factura.totales.total_pasarela_pago) {
          <div class="total-row">
            <span>Comisión pasarela ({{ factura.totales.porcentaje_pasarela_pago }}%):</span>
            <span>${{ factura.totales.total_pasarela_pago.toFixed(2) }}</span>
          </div>
        }
      </div>
    </div>

    <!-- Payment Method -->
    <div class="section">
      <h3>Método de Pago</h3>
      <p><strong>{{ factura.pasarela_pago.nombre }}</strong> - Comisión: {{ factura.pasarela_pago.comision }}%</p>
      @if (factura.pasarela_pago.pasarelaData?.transactionId) {
        <p>ID Transacción: {{ factura.pasarela_pago.pasarelaData.transactionId }}</p>
      }
    </div>

    <!-- Footer -->
    <div class="footer">
      <p>Gracias por su compra en Ticket Éxito</p>
      <p class="small-text">Este documento es un comprobante de compra válido</p>
    </div>
  </div>
}
